import clsx, { ClassValue } from 'clsx'
import { ButtonHTMLAttributes, forwardRef, PropsWithChildren } from 'react'
import { FormattedMessage } from 'react-intl'

import withTestId from '../hoc/withTestId'

export type Theme = 'secondary' | 'light' | 'primary' | 'warning' | 'success' | 'danger' | 'info' | 'dark'

export type Variant = 'light' | 'outline' | 'link' | 'icon'

export type Size = 'sm' | 'lg'

export type HoverEffect = 'elevate-up' | 'elevate-down' | 'scale' | 'rotate-end' | 'rotate-start'

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  isLoading?: boolean
  variant?: Variant
  size?: Size

  hover?: HoverEffect
  theme?: Theme
  active?: Theme

  textColor?: Theme | (string & {})
  iconColor?: Theme | (string & {})
  activeTextColor?: Theme | (string & {})
  bg?: Theme | (string & {})
}

const Button = forwardRef<HTMLButtonElement, PropsWithChildren<ButtonProps>>(
  (
    {
      children,
      isLoading,
      size,
      type = 'button',

      variant,
      theme,
      active,
      activeTextColor,
      iconColor,
      textColor,
      hover,
      bg,

      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const buttonClass = (): ClassValue => {
      const classNames: ClassValue = ['btn']

      switch (variant) {
        case 'light': {
          if (theme && theme !== 'light') {
            classNames.push(`btn-light-${theme}`)
          } else {
            classNames.push('btn-light')
          }

          if (active) {
            classNames.push(`btn-active-light-${active}`)
          }

          break
        }
        case 'link': {
          classNames.push('btn-link')

          break
        }
        case 'outline': {
          classNames.push('btn-outline')

          if (theme) {
            classNames.push(`btn-outline-${theme}`)
          }

          if (active) {
            classNames.push(`btn-active-${active}`)
          }

          break
        }
        case 'icon': {
          classNames.push('btn-icon')
          classNames.push(`btn-${theme}`)

          if (active) {
            classNames.push(`btn-active-${active}`)
          }

          break
        }
        default: {
          classNames.push(`btn-${theme}`)

          if (active) {
            classNames.push(`btn-active-${active}`)
          }

          break
        }
      }

      if (size) {
        classNames.push(`btn-${size}`)
      }

      if (textColor) {
        classNames.push(`btn-color-${textColor}`)
      }

      if (activeTextColor) {
        classNames.push(`btn-active-color-${activeTextColor}`)
      }

      if (iconColor) {
        classNames.push(`btn-icon-${iconColor}`)
      }

      if (hover) {
        classNames.push(`hover-${hover}`)
      }

      if (bg) {
        classNames.push(`btn-bg-${bg}`)
      }

      return classNames
    }

    return (
      <button
        ref={ref}
        {...props}
        // eslint-disable-next-line react/button-has-type
        type={type}
        className={clsx(buttonClass(), className)}
        disabled={isLoading || disabled}
        data-kt-indicator={isLoading ? 'on' : 'off'}
      >
        <span className="indicator-label">{children}</span>

        <span className="indicator-progress">
          {variant !== 'icon' && <span className="me-2">{<FormattedMessage id="indicator.loading" />}</span>}
          <span className="spinner-border spinner-border-sm align-middle" />
        </span>
      </button>
    )
  }
)

const ButtonWithTestId = withTestId(Button, 'button')

export default ButtonWithTestId
